/**
 * Кнопка «E2E тест покупки» — создаёт реальный CryptoBot-инвойс с
 * произвольной суммой (10..100 ₽) для тестирования биллинг-цепочки.
 *
 * Поток:
 *   click → форма (tg_id, сумма, тариф, мес) → POST /admin/test_invoice
 *        → backend создаёт CryptoBot инвойс + запись в payments
 *        → возврат pay_url + mini_app_url
 *        → модалка результата с кнопками «Открыть в Telegram» / «Копировать»
 *
 * После реальной оплаты в CryptoBot штатный webhook
 * /webhooks/cryptobot создаёт подписку и шлёт юзеру + админ-чату.
 *
 * Защита от случайного клика — лимит amount 10..100 ₽ на бэке.
 */
import { useState } from "react";
import {
  Button, Modal, Form, InputNumber, Select, message, Space,
  Typography, Alert, Tag,
} from "antd";
import { ExperimentOutlined, CopyOutlined, SendOutlined } from "@ant-design/icons";
import { API_URL } from "../dataProvider";

interface FormValues {
  tg_id: number;
  amount_rub: number;
  tariff: "basic" | "parental" | "premium";
  months: number;
}

interface InvoiceResult {
  payment_id: number;
  invoice_id: string;
  pay_url: string;
  mini_app_url: string | null;
  amount_rub: number;
  tariff: string;
  months: number;
  expires_in_min: number;
}

const DEFAULT_TG_ID = 433340679; // твой tg_id (INITIAL_ADMIN_TG_ID)

export const TestInvoiceButton = () => {
  const [formOpen, setFormOpen] = useState(false);
  const [resultOpen, setResultOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<InvoiceResult | null>(null);
  const [form] = Form.useForm<FormValues>();

  const submit = async (values: FormValues) => {
    setLoading(true);
    try {
      const r = await fetch(`${API_URL}/admin/test_invoice`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(values),
      });
      const data = await r.json();
      if (!r.ok) {
        throw new Error(data?.detail || `HTTP ${r.status}`);
      }
      setResult(data as InvoiceResult);
      setFormOpen(false);
      setResultOpen(true);
    } catch (e: any) {
      message.error(`Ошибка: ${e.message || "не удалось создать инвойс"}`);
    } finally {
      setLoading(false);
    }
  };

  const copy = (text: string, label: string) => {
    navigator.clipboard.writeText(text)
      .then(() => message.success(`Скопировано: ${label}`))
      .catch(() => message.error("Ошибка копирования"));
  };

  return (
    <>
      <Button
        icon={<ExperimentOutlined />}
        onClick={() => setFormOpen(true)}
        type="dashed"
      >
        E2E тест покупки
      </Button>

      <Modal
        title={<><ExperimentOutlined /> E2E тест покупки на тестовую сумму</>}
        open={formOpen}
        onCancel={() => setFormOpen(false)}
        onOk={() => form.submit()}
        okText="Создать инвойс"
        cancelText="Отмена"
        confirmLoading={loading}
        destroyOnClose
      >
        <Alert
          type="info" showIcon style={{ marginBottom: 16 }}
          message="Реальный платёж в CryptoBot"
          description={
            <>Создаст настоящий счёт на <b>10..100 ₽</b>. После оплаты обычный
              webhook создаст подписку и пришлёт получателю ссылку
              в личку от <Tag>@bemliBot</Tag>.</>
          }
        />
        <Form
          form={form} layout="vertical"
          initialValues={{
            tg_id: DEFAULT_TG_ID, amount_rub: 10,
            tariff: "basic", months: 1,
          }}
          onFinish={submit}
        >
          <Form.Item
            label="Telegram ID получателя"
            name="tg_id"
            rules={[{ required: true, type: "number" }]}
            extra="По умолчанию — твой tg_id. Получатель должен хотя бы раз написать /start боту, иначе не получит сообщение."
          >
            <InputNumber style={{ width: "100%" }} />
          </Form.Item>
          <Form.Item
            label="Сумма (₽)"
            name="amount_rub"
            rules={[{ required: true, type: "number", min: 10, max: 100 }]}
            extra="10..100 ₽ — защита от опечатки. Этой суммой ты реально заплатишь USDT/TON в CryptoBot."
          >
            <InputNumber style={{ width: "100%" }} min={10} max={100} />
          </Form.Item>
          <Form.Item
            label="Тариф (для теста подписка любого типа)"
            name="tariff"
            rules={[{ required: true }]}
          >
            <Select
              options={[
                { value: "basic", label: "Быстрый VPN (basic)" },
                { value: "parental", label: "Родконтроль (parental)" },
                { value: "premium", label: "Premium 4K (premium)" },
              ]}
            />
          </Form.Item>
          <Form.Item
            label="Срок (месяцев)"
            name="months"
            rules={[{ required: true, type: "number", min: 1, max: 12 }]}
          >
            <InputNumber style={{ width: "100%" }} min={1} max={12} />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="✅ Инвойс создан"
        open={resultOpen}
        onCancel={() => setResultOpen(false)}
        footer={null}
        width={620}
      >
        {result && (
          <Space direction="vertical" size="middle" style={{ width: "100%" }}>
            <Alert
              type="success" showIcon
              message={`Платёж #${result.payment_id} · ${result.amount_rub} ₽ · ${
                result.tariff} ${result.months} мес`}
              description={`Инвойс действителен ${result.expires_in_min} мин. После оплаты — webhook → подписка в БД → личка получателю.`}
            />

            <div>
              <Typography.Text strong>Pay URL:</Typography.Text>
              <Typography.Paragraph
                copyable={{ text: result.pay_url }}
                style={{
                  marginTop: 4, padding: 8,
                  background: "#f5f5f5", borderRadius: 4,
                  fontFamily: "monospace", fontSize: 12,
                  wordBreak: "break-all",
                }}
              >
                {result.pay_url}
              </Typography.Paragraph>
            </div>

            {result.mini_app_url && (
              <div>
                <Typography.Text strong>Mini App URL (в Telegram):</Typography.Text>
                <Typography.Paragraph
                  copyable={{ text: result.mini_app_url }}
                  style={{
                    marginTop: 4, padding: 8,
                    background: "#f5f5f5", borderRadius: 4,
                    fontFamily: "monospace", fontSize: 12,
                    wordBreak: "break-all",
                  }}
                >
                  {result.mini_app_url}
                </Typography.Paragraph>
              </div>
            )}

            <Space wrap>
              <Button
                type="primary" icon={<SendOutlined />}
                onClick={() => window.open(result.pay_url, "_blank")}
              >
                Открыть pay-page
              </Button>
              {result.mini_app_url && (
                <Button
                  icon={<SendOutlined />}
                  onClick={() => window.open(result.mini_app_url!, "_blank")}
                >
                  Открыть Mini App в Telegram
                </Button>
              )}
              <Button
                icon={<CopyOutlined />}
                onClick={() => copy(result.pay_url, "pay URL")}
              >
                Копировать pay URL
              </Button>
            </Space>

            <Alert
              type="info"
              showIcon={false}
              style={{ marginTop: 8 }}
              message={
                <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                  💡 Чтобы оплатить — нужны USDT или TON на балансе @CryptoBot.
                  Если их нет: открой @CryptoBot → Кошелёк → Купить → USDT
                  → купи через СБП у P2P-продавца (~5 мин).
                </Typography.Text>
              }
            />
          </Space>
        )}
      </Modal>
    </>
  );
};
